import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import FarmFormFields from 'components/forms/FarmFormFields';

import useFarm from 'hooks/useFarm';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props, UpdateFarmFormSchema } from './types';

import styles from './styles.module.scss';

const UpdateFarmForm: FunctionComponent<Props> = ({
    className = '',
    formValues,
    isClosing,
    onClose,
    setIsClosing,
    testId = TEST_ID
}) => {
    const { updateFarm } = useFarm();
    const { SAVE } = useTranslation();

    const defaultValues: UpdateFarmFormSchema = {
        ...formValues,
        client: {
            label: formValues?.clientName ?? '',
            value: formValues?.clientId ?? ''
        },
        id: formValues?.id ?? '',
        name: formValues?.name ?? ''
    };

    const form = useForm<UpdateFarmFormSchema>({
        defaultValues,
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async (data: UpdateFarmFormSchema) => {
        if (!formValues) {
            return;
        }
        const farm = await updateFarm({
            farm: {
                clientId: data.client.value,
                name: data.name
            },
            id: formValues.id
        });

        if (farm) {
            onClose?.();
        }
    };

    return (
        <VerticalContainer className={`${styles.updateFarmForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form className={styles.form} form={form}>
                    <FarmFormFields />

                    <FormSubmitPanel
                        buttonText={SAVE}
                        className={styles.formSubmitPanel}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default UpdateFarmForm;
